import React, { useRef, useState } from 'react';
import styles from './TcsoStartScreen.module.css';
import { TextInput } from '../common/textInput/TextInput';
import Select, { SingleValue } from 'react-select';
import { RadioInputAnswerOption, RadioInputQuestion } from '../common/radioInputQuestion/RadioInputQuestion';
import BackButton from '../common/button/BackButton';
import { reportingClient, tcsoClient } from '../../api/atomProxyClient';
import { buildPostRequest } from '../../api/apiClient';
import { useTcsoContext, useTcsoScreenContext } from '../../context/TcsoScreenContext';
import { TcsoReportingRequest, validateNewCommsHubGuids } from '../util';
import { TcsoScreen } from './TcsoScreenManager';
import { useChatReasonContext, useHomePageContext } from '../../context/HomePageContext';
import SmoothProgressBar from '../common/progressBar/SmoothProgressBar';
import { TCSO } from '../chat/Chat';
import { startChatSession } from '../chat/startChatSession';

const TcsoStartScreen: React.FC = () => {
  const tcsoInitialTimeout = parseInt(`${process.env.REACT_APP_TCSO_GET_XML_INITIAL_TIMEOUT}`, 10);
  const tcsoTimeout = parseInt(`${process.env.REACT_APP_TCSO_GET_XML_TIMEOUT}`, 10);
  const tcsoMaxRetries = parseInt(`${process.env.REACT_APP_TCSO_GET_XML_MAX_RETRIES}`, 10);
  let tcsoRetries = 0;

  const { setChatReason } = useChatReasonContext();
  const { homePageState, setHomePageState } = useHomePageContext();
  const { setTcsoScreenInputs } = useTcsoScreenContext();
  const { tcsoInputs } = useTcsoContext();

  const [started, setStarted] = useState(false);
  const [completed, setCompleted] = useState(false);
  const [failed, setFailed] = useState(false);
  const [resultTimeout, setResultTimeout] = useState(false);

  const [newChfId, setNewChfId] = useState('');
  const [newChfIdInputError, setNewChfIdInputError] = useState(false);
  const [newChfIdInputErrorMessage, setNewChfIdInputErrorMessage] = useState('');

  const [newGpfId, setNewGpfId] = useState('');
  const [newGpfIdInputError, setNewGpfIdInputError] = useState(false);
  const [newGpfIdInputErrorMessage, setNewGpfIdInputErrorMessage] = useState('');

  const [chfLocation, setChfLocation] = useState('');
  const [chfLocationInputError, setChfLocationInputError] = useState(false);
  const [chfLocationInputErrorMessage, setChfLocationInputErrorMessage] = useState('');

  const [aerialInstalled, setAerialInstalled] = useState('No');

  const inProgress = useRef<boolean>(false);
  const startingTimestamp = useRef<Date>(new Date(Date.parse('1 Jan, 2000')));

  const onTcsoStarted = () => {
    startingTimestamp.current = new Date();
  };

  const onTcsoCompleted = (status: string) => {
    const now = new Date();

    const request: TcsoReportingRequest = {
      journeyId: tcsoInputs.journeyId ?? '',
      commsHubLocation: chfLocation,
      aerialInstalled: aerialInstalled.toUpperCase(),
      newChfId: newChfId,
      newGpfId: newGpfId,
      tcsoResultStatus: status,
      tcsoStartingTimeStamp: startingTimestamp.current.toISOString(),
      tcsoResultTimeStamp: now.toISOString(),
      tcsoTimeTaken: now.getTime() - startingTimestamp.current.getTime(),
    };

    reportingClient.tcso(request).then();
  };

  const radioOptionsAerialInstalled: RadioInputAnswerOption[] = [
    {
      answerValue: 'Yes',
      inputName: 'Yes',
      radioBtnGroupName: 'aerial',
    },
    {
      answerValue: 'No',
      inputName: 'No',
      radioBtnGroupName: 'aerial',
    },
  ];

  const options = [
    {
      value: 'Outside Premises',
      label: 'Outside Premises',
    },
    {
      value: 'Indoors on external wall',
      label: 'Indoors on external wall',
    },
    {
      value: 'Deep indoors',
      label: 'Deep indoors',
    },
    {
      value: 'Basement or Cellar',
      label: 'Basement or Cellar',
    },
    {
      value: 'Unknown',
      label: 'Unknown',
    },
  ];

  const goBack = () => {
    setChatReason('');
    setHomePageState({
      ...homePageState,
      automationInProgress: false,
    });
    setTcsoScreenInputs({
      tcsoScreen: TcsoScreen.TCSO_CHECKLIST_SCREEN,
    });
    setStarted(false);
    setCompleted(false);
    setFailed(false);
    setResultTimeout(false);
    tcsoRetries = 0;
  };

  const buildChatData = () => {
    return `Hi
We’re here to help. I can see you need assistance to complete the TCSO for:

Address:
${tcsoInputs.address}

MPxN:
${tcsoInputs.mpxn}

Old comms hub GUID:
${tcsoInputs.chfDeviceId}

New comms hub GUID:
${newChfId}`;
  };

  const startChatHandler = async () => {
    const outcome = await startChatSession({ message: buildChatData(), mpxn: tcsoInputs.mpxn, chatReason: TCSO });
    if (outcome === 'STARTED') {
      goBack();
    }
  };

  const tcsoStartHandler = () => {
    const { chfError, gpfError } = validateNewCommsHubGuids(newChfId, newGpfId);
    applyNewChfIdError(chfError);
    applyNewGpfIdError(gpfError);
    validateChfLocation(chfLocation);
    if (chfError || gpfError || !chfLocation) {
      return;
    }

    setStarted(true);
    setCompleted(false);
    setFailed(false);
    setResultTimeout(false);

    onTcsoStarted();
    initiateTcso();
  };

  const initiateTcso = () => {
    if (inProgress.current) {
      return;
    }

    const request = {
      identifier: tcsoInputs.mpxn,
      deviceType: 'CHF',
      chfDeviceId: tcsoInputs.chfDeviceId,
      gpfDeviceId: tcsoInputs.gpfDeviceId,
      newChfId: newChfId,
      newGpfDeviceId: newGpfId,
      chfLocation: chfLocation,
      aerialInstall: aerialInstalled.toUpperCase(),
    };

    inProgress.current = true;
    tcsoClient
      .generateXml(buildPostRequest(request))
      .then((httpResponse) => {
        if (httpResponse.status === 422) {
          return httpResponse.json().then((guidErrors: { chfError?: string | null; gpfError?: string | null }) => {
            applyNewChfIdError(guidErrors.chfError ?? '');
            applyNewGpfIdError(guidErrors.gpfError ?? '');
            inProgress.current = false;
            setStarted(false);
            return null;
          });
        }
        if (httpResponse.status >= 400) {
          throw new Error(`L1 TCSO ${httpResponse.status} proxy error.`);
        }
        return httpResponse.json();
      })
      .then((l1Response) => {
        if (l1Response === null) {
          return;
        }
        if (inProgress.current) {
          setTimeout(() => getTcsoResult(l1Response, tcsoInputs.mpxn), tcsoInitialTimeout);
        } else {
          throw new Error('L1 TCSO Internal State Error');
        }
      })
      .catch((e) => {
        inProgress.current = false;
        setCompleted(true);
        setStarted(false);
        setFailed(true);
        setResultTimeout(false);
        tcsoRetries = 0;
        console.warn(e);
        onTcsoCompleted('FAIL');
      });
  };

  const getTcsoResult = (data: any, mpxn: string) => {
    if (!inProgress.current) {
      return;
    }
    const request = buildPostRequest(data);
    tcsoClient
      .getXml(request)
      .then((httpResponse) => {
        if (httpResponse.status === 500) {
          if (inProgress.current) {
            throw new Error('L2 TCSO 500 proxy error, polling will end');
          }
        }
        return httpResponse.json();
      })
      .then((l2Response) => {
        if (l2Response.status === 'FAIL') {
          throw new Error('L2 TCSO Fail result');
        }

        if (l2Response.status === 'PROCESSING') {
          if (inProgress.current && tcsoMaxRetries > tcsoRetries) {
            tcsoRetries += 1;
            setTimeout(() => getTcsoResult(data, mpxn), tcsoTimeout);
          } else {
            inProgress.current = false;
            setCompleted(true);
            setStarted(false);
            setFailed(false);
            setResultTimeout(true);
            tcsoRetries = 0;
            onTcsoCompleted('TIMEOUT');
          }
        }

        if (l2Response.status === 'SUCCESS') {
          inProgress.current = false;
          setCompleted(true);
          setStarted(false);
          setFailed(false);
          setResultTimeout(false);
          tcsoRetries = 0;
          onTcsoCompleted('SUCCESS');
        }
      })
      .catch((e) => {
        inProgress.current = false;
        setCompleted(true);
        setStarted(false);
        setFailed(true);
        setResultTimeout(false);
        tcsoRetries = 0;
        console.warn(e);
        onTcsoCompleted('FAIL');
      });
  };

  const newChfChangeHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
    const formattedValue = formatGuid(event.target.value);
    const { chfError, gpfError } = validateNewCommsHubGuids(formattedValue, newGpfId);
    applyNewChfIdError(chfError);
    if (newGpfId) {
      applyNewGpfIdError(gpfError);
    }

    setNewChfId(formattedValue);
  };

  const chfLocationChangeHandler = (
    selectedChfLocation: SingleValue<{
      value: string;
      label: string;
    }>,
  ) => {
    validateChfLocation(selectedChfLocation?.value ?? '');

    setChfLocation(selectedChfLocation?.value ?? '');
  };

  const newGpfChangeHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
    const formattedValue = formatGuid(event.target.value);
    const { chfError, gpfError } = validateNewCommsHubGuids(newChfId, formattedValue);
    applyNewGpfIdError(gpfError);
    if (newChfId) {
      applyNewChfIdError(chfError);
    }

    setNewGpfId(formattedValue);
  };

  const applyNewChfIdError = (error: string) => {
    setNewChfIdInputError(!!error);
    setNewChfIdInputErrorMessage(error);
  };

  const applyNewGpfIdError = (error: string) => {
    setNewGpfIdInputError(!!error);
    setNewGpfIdInputErrorMessage(error);
  };

  const validateChfLocation = (chfLocation: string) => {
    if (!chfLocation) {
      setChfLocationInputError(true);
      setChfLocationInputErrorMessage('Please select the comms hub location');
    } else {
      setChfLocationInputError(false);
      setChfLocationInputErrorMessage('');
    }
  };

  const matchChfLocation = () => options.find((o) => o.value === chfLocation);

  const formatGuid = (value: string) => {
    const cleanValue = value.replace(/[^0-9a-fA-F]/g, '');
    const limitedValue = cleanValue.slice(0, 16);

    return limitedValue.match(/.{1,2}/g)?.join('-') || limitedValue;
  };

  return (
    <div className={styles.tcsoScreenContainer}>
      {!started && !completed && (
        <div className="tcsoBody">
          <div className={styles.tcsoNewHubContainer}>
            <BackButton onClick={goBack} />
            <div className={'m-t-48'}>
              <TextInput
                label={'What is the NEW CHF GUID?'}
                inputName={'newChfIdInput'}
                inputValue={newChfId}
                onChangeHandler={newChfChangeHandler}
                showError={newChfIdInputError}
                errorMessage={newChfIdInputErrorMessage}
              />
            </div>

            <div className={'m-t-32'}>
              <label className={`${styles.fontFamilyOpenSans} m-b-10`} htmlFor={'select-comms-hub-location-input'}>
                What is the location of the comms hub?
              </label>
              <Select
                id="select-comms-hub-location"
                inputId="select-comms-hub-location-input"
                placeholder="Select Comms Hub location"
                className="basic-single"
                classNamePrefix="select"
                name="select-comms-hub-location"
                isSearchable={true}
                options={options}
                onChange={chfLocationChangeHandler}
                value={matchChfLocation()}
              />
              {chfLocationInputError && <span className="errorMessage">{chfLocationInputErrorMessage}</span>}
            </div>

            <div className={'m-t-22'}>
              <TextInput
                label={'What is the NEW GPF GUID?'}
                inputName={'newGpfIdInput'}
                inputValue={newGpfId}
                onChangeHandler={newGpfChangeHandler}
                showError={newGpfIdInputError}
                errorMessage={newGpfIdInputErrorMessage}
              />
            </div>
            <div className={'m-t-8'}>
              <RadioInputQuestion
                question={'Is there an Aerial installed for the new comms hub?'}
                answers={radioOptionsAerialInstalled}
                onChangeHandler={() => {
                  setAerialInstalled(aerialInstalled === 'Yes' ? 'No' : 'Yes');
                }}
                defaultValue={aerialInstalled}
              />
            </div>
          </div>
        </div>
      )}

      {started && !completed && (
        <>
          <SmoothProgressBar durationMinutes={5} />
          <div className={`${styles.waitingScreen}`}>
            <div className={`${styles.waitingScreenTitle}`}>In progress</div>
            <div className={`${styles.waitingScreenText}`}>This may take 5 minutes, please wait.</div>
          </div>
        </>
      )}

      {!started && completed && !failed && !resultTimeout && (
        <div className={`${styles.waitingScreen}`}>
          <div className={`${styles.tcsoCompletedTitle}`}>TCSO completed</div>
          <div className={`${styles.waitingScreenText}`}>TCSO has been completed successfully</div>
        </div>
      )}

      {!started && completed && failed && (
        <div className={`${styles.waitingScreen}`}>
          <div className={`${styles.waitingScreenTitle}`}>TCSO not completed</div>
          <div className={`${styles.waitingScreenText}`}>The TCSO couldn’t be completed.</div>
          <div className={`${styles.waitingScreenText}`}>Please contact SST</div>
        </div>
      )}

      {!started && completed && resultTimeout && (
        <div className={`${styles.waitingScreen}`}>
          <div className={`${styles.waitingScreenTitle}`}>Something went wrong</div>
          <div className={`${styles.waitingScreenText}`}>We couldn't complete your TCSO request.</div>
          <div className={`${styles.waitingScreenText}`}>Please contact SST</div>
        </div>
      )}

      <div className={'tcsoFooter'}>
        {!started && !completed && (
          <button className={'tcsoButton'} onClick={tcsoStartHandler}>
            Perform TCSO
          </button>
        )}

        {!started && completed && !failed && !resultTimeout && (
          <button className={'tcsoButton'} onClick={goBack}>
            Finish
          </button>
        )}

        {!started && completed && (failed || resultTimeout) && (
          <button className={'tcsoButton'} onClick={startChatHandler}>
            Start Chat
          </button>
        )}
      </div>
    </div>
  );
};

export default TcsoStartScreen;
